<template lang="pug">
.competition
  OverallLeaderboard(:compYear="compYear" v-if="showOverallLeaderboard" v-show="!showingMobileMenu && !showingMobileChat" :divisions="divisions" :competitionID="competitionObj.compId")
  CompetitionHeader(
    v-if="!showingMobileChat"
    :logo="logo"
    :links="headerLinks"
    :base-url="baseUrl"
    :live="state === 'live'"
    @input="showingMobileMenu = $event"
    :logoSize="logoSize"
  )
  slot(name="snipe")
  LivePlayer(
    v-if="activePage === 'live' || activePage === 'tailgate'  || activeEvent"
    :competition="competitionObj"
    :active-stream="activeStream"
    :active-event="activeEvent"
    :live-stream="streams.length > 0"
    :tailgate-stream="tailgateShows.length > 0"
    :replay-stream="replayThumbs.length > 0"
    :events="events.length > 0"
    :event-has-premium="Boolean(premiumSku)"
    :has-ticket="!!ticket"
    :shortsTitle="shortsTitle"
    :supportSplitScreen="supportSplitScreen"
    :showCustomControls="showCustomControls"
    :requirePremium="requirePremium"
    :trial-available="premiumTrialAvailable"
    :active-premium-trial="activePremiumTrial"
    :has-equipment="!!productIds.length"
    :key="livePlayerKey"
    @toggleMobileChat="showingMobileChat = $event"
  )
    template(#live)
      VideoList(:videos="liveStreamThumbs" @select="selectShow($event)"  layout="vertical")
    template(#replaysTab)
      .replay-placeholder(v-if="!replayThumbs.length") {{__('Replays will be available here once they are ready for watching.')}}
      VideoList(v-else :videos="replayThumbs" @select="selectEvent($event)" layout="vertical")
    template(#tailgateTab)
      VideoList(:videos="tailgateShowThumbs" @select="selectShow($event)" layout="vertical")
    template(#equipment)
      .products(v-if="productIds.length")
        .product-list(v-if="productIds.length")
          h3 {{__('{eventName} - Equipment', {eventName: activeEvent.title}, eventNamespace)}}
          AdvancedProductList(:productIds="productIds")
  .main-container-content(v-else :class="{ 'landing-page': activePage === 'landing' }")
    slot(:name="activePage")
    cms(v-if="currentRoute?.query.page" :page-id="currentRoute.query.page" )
  .schedule(v-if="activePage === 'schedule'")
    CompetitionSchedulePage(:competition="competitionObj" :events="displayedEvents" :timeZone="timeZone" :excludeFromEventSchedule="excludeFromEventScheduleTab")
  EventCompetitors(:divisions="divisions" v-if="activePage === 'competitors'")
  EventLandingPage(
    v-if="activePage === 'landing'"
    :base-url="baseUrl"
    :events="events"
    :premium-price="premiumPrice"
    :streams="liveStreamThumbs"
    :playlist-id="playlistId"
    :shorts-playlist-id="shortsPlaylistId"
    :competition="competitionObj"
    :featuredProductLists="productLists"
    :shortsTitle="shortsTitle"
    :showSchedule="showSchedule"
    :timeZone="timeZone"
  )
    template(#landing-page)
      slot(name="landing-page" v-if="!$rogue.appContext || $rogue.featureFlags?.isOn('enable-app-irongamepremium')")
    template(#replays)
     .road-to(v-if="showRoadToVideos")
        shorts(:playlist-id="roadToEventPlaylistId" theme="landscape" :title="roadToEventPLaylistTitle")
     .rewatch(v-else-if="relatedThumbs.length")
        VideoList(:videos="relatedThumbs" @select="selectEvent($event)" :title="`Rewatch the ${title}`")
  .main-container-content
    slot(:name="activePage + '-bottom'")
    slot(name="bottom")
</template>

<script setup lang="ts">
import { ref, reactive, inject, type ComputedRef,  provide, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { Router } from 'vue-router'
import { eventNamespace } from '../../js/constants'
import { useRogue } from '../../js/rogue'
import { useRogueBuilder } from '../../js/rogue-builder'
import LivePlayer from './LivePlayer.vue'
import Event from './Event.vue'
import CompetitionSchedulePage from './CompetitionSchedulePage.vue'
import CompetitionHeader from './CompetitionHeader.vue'
import EventScheduleScroller from './EventScheduleScroller.vue'
import EventLandingPage from './EventLandingPage.vue'
import EventCompetitors from './EventCompetitors.vue'

interface Ticket {
  ticketIdentifier?: string;
  expiration?: string;
  [key: string]: any;
}

const rogue = useRogue()
const route = useRoute()
const rogueBuilder = useRogueBuilder()

const props = withDefaults(defineProps<{
  title: string
  logo?: object
  logoSize?: string
  compId?: string
  pickList?: string
  tabList?: string
  premiumPrice?: string
  compYear?: string
  showOverallLeaderboard?: boolean
  baseUrl?: string
  state?: string
  overallLeaderboard?: string
  premiumSku?: string | null
  premiumTrialId?: string | null
  playlistId?: string
  shortsPlaylistId?: string
  shortsTitle?: string
  events?: any[]
  roadToEventPlaylistId?: string
  roadToEventPLaylistTitle?: string
  divisions?: {
    name?: string
    athletes?: any[]
  }[]
  streams?: any[]
  tailgateShows?: any[]
  links?: any[]
  editPage?: string
  requirePremium?: boolean
  featuredProductLists?: any[]
  supportSplitScreen?: boolean
  showCustomControls?: boolean
  showSchedule?: boolean
  excludeFromEventSchedule?: string
}>(), {
  premiumPrice             : '$20',
  compYear                 : '2022',
  showOverallLeaderboard   : false,
  state                    : 'upcoming',
  shortsTitle              : 'Highlights',
  events                   : () => [],
  roadToEventPlaylistId    : '',
  premiumSku               : null,
  premiumTrialId           : null,
  editPage                 : 'landing',
  requirePremium           : true,
  featuredProductLists     : () => [],
  supportSplitScreen       : false,
  showCustomControls       : false,
  showSchedule             : false,
  excludeFromEventSchedule : '',
  streams                  : () => [],
  tailgateShows            : () => [],
  links                    : () => [],
  athletes                 : () => ([]),
  tabList                  : 'Live Stream, Replays',
})

const tickets = inject<Ticket[]>('tickets', [])
const pictureInPicture = inject<ComputedRef<any>>('pictureInPicture', computed(() => null))
const showPictureInPicture = inject<(param?: any) => void>('showPictureInPicture', () => {})
const showOverlay = inject('showOverlay', () => {})
const hideOverlay = inject('hideOverlay', () => {})

const loaded = ref(false)
const activeStreamKey = ref<string | null>(null)
const showingMobileMenu = ref(false)
const showingMobileChat = ref(false)
const streamControls = reactive({})
const livePlayerKey = ref(0)
const time = ref(new Date())
let interval: ReturnType<typeof setInterval> | null = null

const currentRoute = computed(() => {
  return route || rogue.router?.currentRoute?.value
})

// Divisions excluded from the "Event Schedule" tab
const excludeFromEventScheduleTab = computed(() =>
  props.excludeFromEventSchedule.split(',').map(division => division.trim()).filter(Boolean))

const productLists = computed(() => {
  return props.featuredProductLists.filter(l => 
    !l.site || (rogue?.site?.code && l.site.includes(rogue.site.code))
  )
})

const athletes = computed(() => {
  return props.divisions?.flatMap((division) => division.athletes) || []
})

const competitionObj = computed(() => ({
  title            : props.title,
  state            : props.state,
  shortsPlaylistId : props.shortsPlaylistId,
  playlistId       : props.playlistId,
  compId           : props.compId,
  pickList         : props.pickList,
  tabList          : props.tabList,
  compYear         : props.compYear,
  baseUrl          : props.baseUrl,
  requirePremium   : props.requirePremium,
}))

const purchasedTicket = computed(() => {
  if (!tickets.length) {
    return undefined
  }
  
  return tickets.find(t => 
    t.ticketIdentifier === props.compId || 
    t.ticketIdentifier === `${props.compId}-AU`
  )
})

const ticket = computed(() => purchasedTicket.value || activePremiumTrial.value)

const liveStream = computed(() => {
  return liveStreams.value.find(s => s.id === activeStreamKey.value)
})

const activeStream = computed(() => {
  return activeStreams.value.find(s => s.id === activeStreamKey.value)
})

const streamLinks = computed(() => {
  return props.streams.map(stream => stream.streamLinks)
})

const timeZone = computed(() => {
  const competitionTimezones: Record<string, string> = {
    'ri2026' : 'Etc/Greenwich',
    'ri2025' : 'Etc/Greenwich',
    'ri2024' : 'Etc/Greenwich',
    'ri2023' : 'America/Chicago',
  }
  
  return competitionTimezones[props.compId as string] || 'America/new_york'
})

const activePage = computed(() => {
  if (rogueBuilder) return props.editPage

  if (currentRoute.value?.path.indexOf(props.baseUrl as string) === -1) return ''

  if (currentRoute.value?.query.page) return currentRoute.value.query.page as string

  const id = currentRoute.value?.path.replace(props.baseUrl as string, '').replace(/^\//, '')
  
  return id ? decodeURIComponent(id) : 'landing'
})

const liveStreamThumbs = computed(() => {

  return props.streams.map(stream => ({
    name      : stream.name,
    id        : stream.streamLinks?.[0]?.videoId,
    eventName : props.title,
    thumbnail : stream.thumbnail,
    live      : stream.status === 'Live' && (!stream.startDate || new Date(stream.startDate) < time.value),
    // A Live stream whose start time hasn't arrived yet is shown as upcoming too
    upcoming  : stream.status === 'Upcoming' || (stream.status === 'Live' && !!stream.startDate && new Date(stream.startDate) >= time.value),
    isActive  : activePage.value === 'live' && stream.streamLinks?.[0]?.videoId === activeStreamKey.value,
    link      : `${props.baseUrl}/live?stream=${encodeURIComponent(stream.name)}`,
    paid      : streamLinks.value.length && 
          streamLinks.value.every(link => link && ['Cloudflare', 'YBVR'].includes(link.type) && link.isPremium) && 
          props.requirePremium,
  }))
})

const replayThumbs = computed(() => {
  if (!loaded.value) return []

  return props.events
    .filter(event => event.thumbnail && event.replayId)
    .filter(event => !event.time || Date.parse(event.time) < Date.now())
    .map(event => ({
      name      : event.title,
      eventName : props.title,
      live      : false,
      isActive  : activePage.value !== 'live' && activeEvent.value === event && event.title === activeStreamKey.value,
      thumbnail : event.thumbnail,
      link      : `${props.baseUrl}/${event.division}/${event.eventId}`.toLowerCase(),
      paid      : false,
    }))
})

const tailgateShowThumbs = computed(() => {
  if (!props.tailgateShows.length) return []

  return props.tailgateShows.map(tailgate => ({
    name      : tailgate.name,
    eventName : tailgate.title,
    id        : tailgate.videoId,
    live      : false,
    isActive  : activePage.value === 'tailgate' && tailgate.videoId === activeStreamKey.value,
    thumbnail : tailgate.thumbnail,
    link      : `${props.baseUrl}/tailgate?show=${encodeURIComponent(tailgate.name)}`,
    paid      : false,
  }))
})

const replayStreams = computed(() => {
  if (!loaded.value) return []

  return props.events
    .filter(event => event.thumbnail && event.replayId)
    .filter(event => !event.time || Date.parse(event.time) < Date.now())
    .map(event => {
      const ytReplay = {
        name         : 'Free Cam',
        thumbnail    : event.thumbnail,
        type         : 'YouTube',
        videoId      : event.replayId,
        startSeconds : event.replayStart,
        endSeconds   : event.replayEnd,
      }
      const streamLinksList = event.eventReplays?.length > 0 ? [ytReplay, ...event.eventReplays] : [ytReplay]

      return {
        name             : event.title,
        competitionTitle : props.title,
        thumbnail        : event.thumbnail,
        shortsPlaylistId : event.shortsPlaylistId,
        eventId          : event.eventId,
        id               : event.title,
        division         : event.division,
        streamLinks      : streamLinksList.map(sl => ({
          name           : sl.name,
          description    : event.description,
          type           : sl.type,
          videoId        : sl.videoId,
          videoSrcUrl    : sl.videoSrcUrl,
          thumbnail      : sl.thumbnail,
          _data          : sl,
          overlayMessage : null,
          active         : true,
          startDate      : null,
          controls       : getStreamControls(sl.videoId || sl.videoSrcUrl || `${event.title}_${sl.name}`),
          paid           : ['Cloudflare', 'YBVR'].includes(sl.type) && sl.isPremium && props.requirePremium,
          start          : sl.startSeconds ? parseInt(sl.startSeconds) : getStartSeconds(sl.videoId || sl.videoSrcUrl, event.time),
          end            : sl.endSeconds ? parseInt(sl.endSeconds) : null,
          hasAccess      : sl.type === 'YouTube' || !sl.isPremium || ticket.value !== undefined,
        })),
        hasAccess  : true,
        live       : false,
        streamType : 'Replays',
        startDate  : '2023-10-26T13:00:00.000Z',
      }
    })
})

const tailgateStreams = computed(() => {
  if (!props.tailgateShows.length) return []

  return props.tailgateShows
    .filter(tailgateShow => tailgateShow.thumbnail)
    .map(tailgateShow => {
      const ytTailgate = {
        name      : 'Tailgate Show',
        thumbnail : tailgateShow.thumbnail,
        type      : 'YouTube',
        videoId   : tailgateShow.videoId,
      }
      const links = tailgateShow?.length > 0 ? [ytTailgate, ...tailgateShow] : [ytTailgate]

      return {
        name             : tailgateShow.name,
        competitionTitle : tailgateShow.title,
        thumbnail        : tailgateShow.thumbnail,
        id               : tailgateShow.videoId,
        shortsPlaylistId : null,
        tailgateShowId   : null,
        division         : null,
        streamLinks      : links.map(sl => ({
          name        : tailgateShow.name,
          description : tailgateShow.description,
          type        : 'YouTube',
          videoId     : sl.videoId,
          videoSrcUrl : null,
          thumbnail   : sl.thumbnail,
          active      : true,
          controls    : getStreamControls(sl.videoId || `${sl.title}_${sl.name}`),
          hasAccess   : true,
        })),
        hasAccess  : true,
        streamType : 'Tailgate Show',
      }
    })
})

const liveStreams = computed(() => {

  return props.streams.map(s => ({
    ...s,
    competitionTitle : props.title,
    id               : s.streamLinks?.[0]?.videoId,
    hasAccess        : Boolean(s.streamLinks?.some(sl => sl.type === 'YouTube' || !sl.isPremium) || ticket.value !== undefined),
    live             : props.state === 'live' && (!s.startDate || new Date(s.startDate) < time.value),
    startDate        : s.startDate || '2023-10-26T13:00:00.000Z',
    streamLinks      : s.streamLinks?.map(streamLink => ({
      ...streamLink,
      controls       : getStreamControls(`${s.name}_${streamLink.name}`),
      paid           : ['Cloudflare', 'YBVR'].includes(streamLink.type) && streamLink.isPremium && props.requirePremium,
      fullscreenLink : `${props.baseUrl}/live?stream=${encodeURIComponent(s.name)}`,
      hasAccess      : streamLink.type === 'YouTube' || !streamLink.isPremium || ticket.value !== undefined,
    })) || [],
  }))
})

const activeStreams = computed(() => {
  return [...liveStreams.value, ...replayStreams.value, ...tailgateStreams.value]
})

const activeEvent = computed(() => {
  if (activePage.value === 'live') {
    if (props.state !== 'live' || !loaded.value) return undefined

    return props.events
      .filter(e => ['Individual', 'CrossFit', 'Strongman', 'Strongwoman'].includes(e.division))
      .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
      .find(event => new Date(event.time) < new Date(time.value || Date.now()))
  }

  return props.events.find(e => `${e.division}/${e.eventId}`.toLowerCase() === activePage.value.toLowerCase())
})

const pipStream = computed(() => {
  return liveStream.value?.streamLinks?.find(l => l.controls.playing && l.active) || 
         liveStream.value?.streamLinks?.find(l => l.hasAccess && l.active)
})

const relatedThumbs = computed(() => {
  if (!loaded.value) return []

  return props.events
    .filter(event => event !== activeEvent.value && event.thumbnail && event.replayId)
    .filter(event => !event.time || Date.parse(event.time) < Date.now())
    .map(event => ({
      name      : event.title,
      eventName : props.title,
      thumbnail : event.thumbnail,
      link      : `${props.baseUrl}/${event.division}/${event.eventId}`.toLowerCase(),
    }))
})

const showRoadToVideos = computed(() => !!props.roadToEventPlaylistId)

const showRewatchVideos = computed(() => activePage.value === 'live' && relatedThumbs.value.length > 0)

const productIds = computed(() => {
  const siteProductList = (activeEvent.value || {}).eventProductLists?.find(l => 
    !l.site || (rogue?.site?.code && l.site.includes(rogue.site.code))
  )

  if (siteProductList) {
    return siteProductList.products.map(p => p.productId)
  }

  return ((activeEvent.value || {}).productIds || '').split(',')
    .filter(id => !!id)
    .map(id => parseInt(id.trim(), 10))
})

const headerLinks = computed(() => {
  return props.links
    .filter(l => l.active !== false)
    .map(l => ({
      text   : l.text,
      active : l.path === activePage.value,
      path   : `${props.baseUrl}/${l.path}`.toLowerCase(),
    }))
})

const displayedEvents = computed(() => {
  return competitionObj.value.state === 'past'
    ? props.events.filter(e => e.division && ['Strongman', 'Strongwoman', 'Individual', 'CrossFit', 'Legends'].includes(e.division.replace(' Events', '')))
    : props.events
})

const premiumTrialAvailable = computed(() => {
  if (!tickets.length) {
    return undefined
  }
  
  const trialAlreadyUsed = tickets.some(t => t.ticketIdentifier === props.premiumTrialId)
  
  return !!props.premiumTrialId && !trialAlreadyUsed && props.state === 'live' && !rogue?.appContext
})

const activePremiumTrial = computed(() => {
  if (purchasedTicket.value) return undefined

  if (!tickets.length) {
    return undefined
  }

  return tickets.find(t => 
    t.ticketIdentifier === props.premiumTrialId && 
    t.expiration &&
    Date.parse(t.expiration) > (time.value as Date).getTime()
  )
})

onMounted(() => {
  interval = setInterval(() => time.value = new Date(), 1000)
  loaded.value = true
})

onBeforeUnmount(() => {
  if (interval) {
    clearInterval(interval)
  }
})

const setActiveStream = () => {
  if (props.tabList === 'Replays' && activePage.value === 'live') {
    activeStreamKey.value = props.events[0]?.title

    return
  }

  if (activePage.value !== 'live') {
    if (activeEvent.value) {
      activeStreamKey.value = activeEvent.value.title
    } else {
      const showQuery = currentRoute.value?.query.show as string
      activeStreamKey.value = props.tailgateShows.find(s => s.name === showQuery)?.videoId
        ?? props.tailgateShows[0]?.videoId
    }
    
    return
  }

  const streamQuery = currentRoute.value?.query.stream as string
  const byQuery    = props.streams.find(s => s.name === streamQuery)
  const byStatus   = props.streams.find(s => s.status !== 'Past') ?? props.streams[0]
  activeStreamKey.value = (byQuery ?? byStatus)?.streamLinks?.[0]?.videoId
}

const selectEvent = (video: { name: string; link: string }, forceLivePlayerUpdate = true) => {
  activeStreamKey.value = video.name
  rogue?.router?.push(video.link)

  if (forceLivePlayerUpdate) {
    livePlayerKey.value += 1
  }
}

const selectShow = (video: { id: any; link: string }) => {
  activeStreamKey.value = video.id
  rogue?.router?.push(video.link)
}

const getStreamControls = (streamName: string) => {
  if (!streamControls[streamName]) {
    streamControls[streamName] = reactive({ playing: false, volume: 100 })
  }
  
  return streamControls[streamName]
}

const getStartSeconds = (videoIdOrUrl: string, eventStartTime: string) => {
  const liveStreamMatch = props.streams
    .flatMap(s => s.streamLinks || [])
    .filter(sl => !!sl.startDate)
    .find(sl => sl.videoId === videoIdOrUrl || sl.videoSrcUrl === videoIdOrUrl)

  if (liveStreamMatch && eventStartTime) {
    const streamStartDate = Date.parse(liveStreamMatch.startDate)
    const eventStartDate = Date.parse(eventStartTime)

    if (eventStartDate > streamStartDate) {
      return (eventStartDate - streamStartDate) / 1000
    }
  }
  
  return null
}

const updateSelectedEvent = (streamLink: any, currentTime: number) => {
  const allReplayStreams = replayStreams.value.flatMap(e => 
    e.streamLinks.map((s: any) => ({ stream: s, event: e }))
  )

  const selection = allReplayStreams
    .filter(x => x.stream.videoId === streamLink.videoId)
    .filter(x => x.stream.start && x.stream.start <= currentTime)
    .sort((a, b) => (a.stream.start || 0) - (b.stream.start || 0))
    .at(-1)

  if (selection && activeEvent.value?.title !== selection.event.name) {
    streamLink.controls.playing = true
    const newEvent = {
      name : selection.event.name,
      link : `${props.baseUrl}/${selection.event.division}/${selection.event.eventId}`.toLowerCase(),
    }
    selectEvent(newEvent, false)
  }
}

watch(
  () => currentRoute.value?.path,
  (newVal) => {
    if (!newVal) {return}
    setActiveStream()
    
    if (props.state === 'live') {
      if (newVal.indexOf('invitational') > 0 && (activePage.value === 'live' || activeEvent.value) && showPictureInPicture) {
        showPictureInPicture()
      } else if (showPictureInPicture && !pictureInPicture && pipStream.value) {
        pipStream.value.controls.playing = true
        showPictureInPicture(pipStream.value)
      }
    }
  },
  { immediate: true }
)

// Watch activePage
watch(
  () => activePage.value,
  (newVal) => {
    if (typeof window === 'undefined') return
    if (!activeEvent.value && newVal !== 'tailgate' && newVal !== 'landing' && (!props.links.some(l => l.path === newVal))) {
      rogue?.router?.push(props.baseUrl as string)
    }
  },
  { immediate: true }
)

// Watch showingMobileMenu
watch(
  () => showingMobileMenu.value,
  (newVal) => {
    if (newVal) {
      showOverlay('CompetitionMobileMenu')
    } else {
      hideOverlay()
    }
  }
)

// Provide
provide('updateSelectedEvent', updateSelectedEvent)

// Exposed for parent access via refs
defineExpose({
  updateSelectedEvent,
  livePlayerKey,
  loaded,
})

</script>
<style lang="scss" scoped>
.landing-page{
  min-height: 50vh;
}
.rewatch {
margin-top: 3em;
margin-bottom: 3em;
}

.road-to {
margin-top: 3em;
margin-bottom: 3em;
}

.replay-placeholder {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: $size-s;
  line-height: $line-height-l;
  font-weight: $weight-normal;
  color: $color-gray-dark;
  text-align: center;
  padding: $space-s;
}
</style>
